import { buffers, redact } from './recorder';

export const BUILD = {
  gitSha: (import.meta.env.VITE_GIT_SHA as string | undefined) || 'unknown',
  buildTime: (import.meta.env.VITE_BUILD_TIME as string | undefined) || 'unknown',
  environment: import.meta.env.MODE,
};

/** A failed request older than this is not attached: it is not what the user is reporting (issue 21). */
export const NET_WINDOW_MS = 10 * 60 * 1000;

export function collectContext(extra?: { routeFileHint?: string | null; appState?: unknown; user?: Record<string, unknown> | null }) {
  const nav = navigator as Navigator & { connection?: { effectiveType?: string }; deviceMemory?: number };
  const t0 = Date.now();
  const navTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  const mem = (performance as Performance & { memory?: { usedJSHeapSize?: number } }).memory;
  return redact({
    schema: 'feedback:v1',
    reportedAt: new Date().toISOString(),
    page: {
      url: location.href,
      pathname: location.pathname,
      referrer: document.referrer || null,
      routeFileHint: extra?.routeFileHint ?? null,
    },
    build: { ...BUILD, appVersion: '0.1.0' },
    user: extra?.user ?? null,
    client: {
      userAgent: navigator.userAgent,
      platform: nav.platform,
      language: navigator.language,
      locale: Intl.DateTimeFormat().resolvedOptions().locale,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      viewport: { w: innerWidth, h: innerHeight },
      screen: { w: screen.width, h: screen.height },
      devicePixelRatio,
      prefersColorScheme: matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
      online: navigator.onLine,
      connectionType: nav.connection?.effectiveType ?? null,
      deviceMemory: nav.deviceMemory ?? null,
    },
    performance: {
      sinceLoadMs: Math.round(performance.now()),
      domContentLoadedMs: navTiming ? Math.round(navTiming.domContentLoadedEventEnd) : null,
      loadMs: navTiming ? Math.round(navTiming.loadEventEnd) : null,
      usedJSHeapSize: mem?.usedJSHeapSize ?? null,
    },
    errors: buffers.errors,
    networkFailures: buffers.net
      .filter((n) => t0 - n.t <= NET_WINDOW_MS)
      .map((n) => ({ ...n, ago: `${((t0 - n.t) / 1000).toFixed(1)}s` })),
    breadcrumbs: buffers.crumbs.map((c) => ({ ...c, ago: `${((t0 - c.t) / 1000).toFixed(1)}s` })),
    appState: extra?.appState ?? null,
  });
}

type ImageCaptureCtor = new (t: MediaStreamTrack) => { grabFrame(): Promise<ImageBitmap> };

export async function captureScreenshot(): Promise<Blob | null> {
  if (!navigator.mediaDevices?.getDisplayMedia) return null;
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' } as MediaTrackConstraints,
    audio: false,
    preferCurrentTab: true,
  } as DisplayMediaStreamOptions);
  try {
    const track = stream.getVideoTracks()[0];
    const IC = (window as unknown as { ImageCapture?: ImageCaptureCtor }).ImageCapture;
    let bitmap: ImageBitmap;
    if (IC) {
      bitmap = await new IC(track).grabFrame();
    } else {
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      bitmap = await createImageBitmap(video);
      video.pause();
    }
    return await downscale(bitmap, 1600, 'image/png');
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

/** Long edge capped at `max`. Screenshots stay PNG; photos become JPEG q0.85. */
export async function downscale(src: ImageBitmap | Blob, max = 1600, type?: string): Promise<Blob> {
  const bmp = src instanceof Blob ? await createImageBitmap(src) : src;
  const outType = type ?? (src instanceof Blob && src.type === 'image/png' ? 'image/png' : 'image/jpeg');
  const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * scale);
  canvas.height = Math.round(bmp.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas not available');
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  return new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error('could not encode image'))), outType, outType === 'image/jpeg' ? 0.85 : undefined),
  );
}
